/**
 * The asset schemas, in the browser.
 *
 * `api/schemas/asset/*.json` IS the contract (backend standard §20). The service validates
 * against it, the Node contract suite validates against it, and — from prompt 15.2 — the
 * authoring FORM is generated from it. That last one is the reason these files have to be
 * reachable from a bundle at all: a form and a validation rule that are two expressions of the
 * same contract will drift, and the one that drifts silently is the form.
 *
 * They are a MIRROR, byte-identical and checked (`pnpm schemas:check`), for the same reason
 * `fixtures/` is mirrored into packages/testing: a workspace package cannot import from outside
 * its own tree, and two copies that are allowed to disagree eventually will.
 *
 * # What this is not
 *
 * It is not a validator. Nothing here evaluates a document against a schema — that needs AJV,
 * which is scoped to tools/contracts and may not enter the console (CLAUDE.md rule 1,
 * allowed-dependencies.json). The service is the validator, and its `ValidationIssue` triples are
 * what the editor renders. What the schema is used for here is STRUCTURE: which fields exist,
 * what they are called, what a value may be, and which are required.
 *
 * # Where the documents are
 *
 * This entry holds the TYPES and the helpers; the JSON documents themselves (`assetSchemas`,
 * `assetSchemaFor`) are `@eis/core/asset-schemas`. The root of `@eis/core` is a static dependency
 * of the shell, so anything it exports is hoisted into a module chunk every user downloads — and
 * only the authoring editors (a lazy chunk of their own) and the mock read the documents
 * (prompt 26 phase 26.1: the v1-aligned `tarif_deckung` schema pushed `module-billing` over budget).
 */

/** A JSON Schema node, as much of it as the form generator actually reads. */
export interface AssetSchemaNode {
  readonly title?: string;
  readonly description?: string;
  readonly type?: string;
  readonly enum?: readonly string[];
  readonly properties?: Readonly<Record<string, AssetSchemaNode>>;
  readonly required?: readonly string[];
  readonly items?: AssetSchemaNode;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly minItems?: number;
  readonly pattern?: string;
  readonly $ref?: string;
  readonly $defs?: Readonly<Record<string, AssetSchemaNode>>;
  readonly additionalProperties?: boolean | AssetSchemaNode;
  // `const` and `oneOf` arrived with the billing_config asset (SDD-015 §5.5): its two profiles are
  // a `oneOf` selected by a `const` discriminator, and its payment term is a `oneOf` of two objects.
  readonly const?: string;
  readonly oneOf?: readonly AssetSchemaNode[];
}

/**
 * The asset TYPES, which are the part of an asset's name before the version suffix.
 *
 * `insurance_core.v2` is a different asset from `insurance_core.v1`, not a revision of it — the
 * suffix is part of the NAME (proto comment on CoreConfig), which is what makes "published assets
 * are immutable" a statement about rows rather than a policy somebody enforces.
 */
export const assetTypes = [
  'insurance_core',
  'group_core',
  'tarif_deckung',
  'lead_lifecycle',
  'billing_config',
] as const;
export type AssetType = (typeof assetTypes)[number];

/** The types a Tarif carries, and the ones a Group Tarif does. Different owners, different sets. */
// `billing_config` is the one type BOTH carry (SDD-015 B-7): the tarif's asset is total, the group
// tarif's a field-wise override resolved at read.
export const tarifAssetTypes = [
  'insurance_core',
  'tarif_deckung',
  'lead_lifecycle',
  'billing_config',
] as const;
export const groupTarifAssetTypes = ['group_core', 'tarif_deckung', 'billing_config'] as const;

export const isAssetType = (value: string): value is AssetType =>
  (assetTypes as readonly string[]).includes(value);

/**
 * `insurance_core.v2` → `insurance_core`. Returns undefined for a name that names no known type,
 * which the editor renders as "this asset has no schema the console knows" rather than guessing.
 */
export function assetTypeOf(name: string): AssetType | undefined {
  const dot = name.indexOf('.');
  const type = dot === -1 ? name : name.slice(0, dot);
  return isAssetType(type) ? type : undefined;
}

/**
 * Follows a `$ref` inside the schema it came from. Only local `#/$defs/...` refs exist in these
 * documents, and a ref that leaves the document is returned unresolved rather than fetched: a
 * form generator that made a network call to draw a label would be a different kind of program.
 */
export function resolveRef(root: AssetSchemaNode, node: AssetSchemaNode): AssetSchemaNode {
  const ref = node.$ref;
  if (ref === undefined) return node;
  const prefix = '#/$defs/';
  if (!ref.startsWith(prefix)) return node;
  const target = root.$defs?.[ref.slice(prefix.length)];
  if (target === undefined) return node;
  // A `$ref` alongside siblings (`{"$ref": …, "description": …}`) is legal in 2020-12 and the
  // asset schemas use it: the local description wins over the target's.
  return { ...target, ...node, $ref: undefined };
}
